// /var/www/oszczedzam/client/src/components/dashboard/BalanceSummary.js
import React from 'react';
import { 
  Card, CardContent, Typography, Grid, Box,
  Paper, Divider, useTheme, alpha
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import CountUp from 'react-countup';

function BalanceSummary({ income, expenses, balance }) {
  const theme = useTheme();
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { 
      style: 'currency', 
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        mb: 4, 
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.1)
      }}
    >
      <Typography variant="h6" gutterBottom fontWeight="medium">Bilans finansowy</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card 
            variant="outlined"
            sx={{ 
              borderRadius: 3,
              borderColor: balance >= 0 ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.error.main, 0.5),
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box 
                  sx={{ 
                    backgroundColor: balance >= 0 ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 1
                  }}
                >
                  <AccountBalanceIcon color={balance >= 0 ? "success" : "error"} />
                </Box>
                <Typography variant="subtitle1" color="text.secondary">Saldo</Typography>
              </Box>
              <Typography 
                variant="h4" 
                color={balance >= 0 ? 'success.main' : 'error.main'}
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                <CountUp
                  end={Math.abs(balance)}
                  duration={1.5}
                  separator=" "
                  decimals={2}
                  decimal=","
                  prefix={balance < 0 ? "-" : ""}
                  suffix=" zł"
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {balance >= 0 ? "Na plusie" : "Na minusie"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            variant="outlined"
            sx={{ 
              borderRadius: 3,
              borderColor: alpha(theme.palette.success.main, 0.5),
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box 
                  sx={{ 
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 1
                  }}
                >
                  <TrendingUpIcon sx={{ color: 'success.main' }} />
                </Box>
                <Typography variant="subtitle1" color="text.secondary">Przychody</Typography>
              </Box>
              <Typography 
                variant="h4" 
                color="success.main"
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                <CountUp
                  end={income}
                  duration={1.5}
                  separator=" "
                  decimals={2}
                  decimal=","
                  suffix=" zł"
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Całkowite przychody
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            variant="outlined"
            sx={{ 
              borderRadius: 3,
              borderColor: alpha(theme.palette.error.main, 0.5),
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box 
                  sx={{ 
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 1
                  }}
                >
                  <TrendingDownIcon sx={{ color: 'error.main' }} />
                </Box>
                <Typography variant="subtitle1" color="text.secondary">Wydatki</Typography>
              </Box>
              <Typography 
                variant="h4" 
                color="error.main"
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                <CountUp
                  end={expenses}
                  duration={1.5}
                  separator=" "
                  decimals={2}
                  decimal=","
                  suffix=" zł"
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Całkowite wydatki
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default BalanceSummary;